// src/scenes/ClassifyScene.js
import Phaser from 'phaser';
import BaseMenuScene from "./BaseMenuScene.js"; // Extend BaseMenuScene for background/resize
import MenuUIUtils from "../ui/MenuUIUtils.js";
import claudeAPIService from "../services/claudeAPIService.js";
import sceneManager from "../utils/SceneManager.js";
import { auth } from '../firebaseConfig.js'; // Import auth for back button logic
import { hasSpecialAccess } from "../services/specialAccessService.js"; // Import special access check
import { saveScoreToFirestore } from "../services/authService.js"; // Import score saving function
// Import showGameOverUI if using that directly, otherwise MultiplayerGameOverScene handles it
// import { showGameOverUI } from "../ui/uiUtils.js";

const CATEGORY_1 = "Hardware";
const CATEGORY_2 = "Software";
const WORDS_TO_CLASSIFY = 10;

class ClassifyScene extends BaseMenuScene {
    constructor() {
        super({ key: "ClassifyScene" });
        this.words = [];
        this.currentWordIndex = 0;
        this.score = 0;
        this.isLoading = true;
        this.currentWordObject = null;
        this.feedbackText = null;
        this.categoryBox1 = null;
        this.categoryBox2 = null;
        this.dropZone1 = null;
        this.dropZone2 = null;
        this.uiElements = {}; // Use this to store created elements
        this.claudeService = new claudeAPIService();
    }

    init() {
        super.init(); // Call parent init
        console.log(`[${this.scene.key}] Initializing...`);
        this.words = [];
        this.currentWordIndex = 0;
        this.score = 0;
        this.isLoading = true;
        this.currentWordObject = null;
        this.feedbackText = null;
        this.categoryBox1 = null;
        this.categoryBox2 = null;
        this.dropZone1 = null;
        this.dropZone2 = null;
        this.uiElements = {}; // Reset UI elements store
    }

    async create() {
        super.create(); // Creates background, sets up resize listener
        console.log(`[${this.scene.key}] Create START`);
        this.clearMenuItems(); // Clear base menu items if any

        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const centerX = width / 2;

        // Title
        this.uiElements.title = this.createThemedText(centerX, 50, "Classify Tech!", 'sizeXl', MenuUIUtils.COLORS.primary);

        // Score Display
        this.uiElements.scoreText = this.createThemedText(width - this.padding, this.padding, `Score: 0`, 'sizeMd', MenuUIUtils.COLORS.textLight, 1).setOrigin(1, 0);

        // Loading Indicator
        this.uiElements.loadingText = this.createThemedText(centerX, height / 2, "Loading words...", 'sizeLg', MenuUIUtils.COLORS.warning);

        // Create Category Boxes (initially hidden or positioned off-screen)
        this.createCategoryBoxes();
        if (this.categoryBox1) this.categoryBox1.setVisible(false);
        if (this.categoryBox2) this.categoryBox2.setVisible(false);

        // Back Button
        this.uiElements.backButton = this.createStyledButton(
            this.padding + 60, this.padding + 20, // Top-left corner
            "Back",
            () => this.returnToPreviousMenu(),
            MenuUIUtils.COLORS.secondary_dark, // Darker cyan
            100, 40 // Smaller size
        );

        // Start with fallback words immediately for responsive gameplay
        this.initializeFallbackWords();
        
        // Fetch words from Claude in background (non-blocking)
        this.fetchWordsInBackground();

        // Set up drag listeners AFTER fetching words and creating UI
        this.input.on('dragstart', (pointer, gameObject) => {
            if (gameObject === this.currentWordObject) {
                this.children.bringToTop(gameObject); // Bring word to front
                gameObject.setData('isDragging', true);
                gameObject.setData('startX', gameObject.x);
                gameObject.setData('startY', gameObject.y);
            }
        });

        this.input.on('drag', (pointer, gameObject, dragX, dragY) => {
            if (gameObject === this.currentWordObject) {
                gameObject.x = dragX;
                gameObject.y = dragY;
            }
        });

        this.input.on('dragend', (pointer, gameObject, dropped) => {
             if (gameObject === this.currentWordObject) {
                 gameObject.setData('isDragging', false);
                 // If not dropped on a zone, snap back with tween
                 if (!dropped) {
                     this.tweens.add({
                         targets: gameObject,
                         x: gameObject.getData('startX'),
                         y: gameObject.getData('startY'),
                         duration: 200,
                         ease: 'Power1'
                     });
                 }
             }
        });

        this.input.on('drop', (pointer, gameObject, dropZone) => {
            if (gameObject === this.currentWordObject) {
                const correctCategory = gameObject.getData('category');
                const droppedCategory = dropZone.getData('category');
                console.log(`Dropped "${gameObject.text}" (Correct: ${correctCategory}) onto Zone ${droppedCategory}`);
                this.handleWordDrop(correctCategory === droppedCategory, gameObject);
            }
        });

        // Initial positioning
        this.handleResize();
        console.log(`[${this.scene.key}] Create FINISHED`);
        
     
    }

    initializeFallbackWords() {
        // Start immediately with fallback words for responsive gameplay
        console.log("ClassifyScene: Initializing with fallback words for immediate gameplay");
        this.words = [
            { word: "CPU", category: CATEGORY_1 },
            { word: "Algorithm", category: CATEGORY_2 },
            { word: "RAM", category: CATEGORY_1 },
            { word: "JavaScript", category: CATEGORY_2 },
            { word: "GPU", category: CATEGORY_1 },
            { word: "Operating System", category: CATEGORY_2 },
            { word: "Motherboard", category: CATEGORY_1 },
            { word: "CSS", category: CATEGORY_2 },
            { word: "Hard Drive", category: CATEGORY_1 },
            { word: "Compiler", category: CATEGORY_2 }
        ];
        
        // Shuffle fallback words for variety
        Phaser.Utils.Array.Shuffle(this.words);
        this.words = this.words.slice(0, WORDS_TO_CLASSIFY);
        
        this.isLoading = false;
        if (this.uiElements.loadingText) this.uiElements.loadingText.destroy();

        // Make boxes visible now that words are loaded
        if (this.categoryBox1) this.categoryBox1.setVisible(true);
        if (this.categoryBox2) this.categoryBox2.setVisible(true);

        this.displayNextWord();
    }
    
    async fetchWordsInBackground() {
        // Non-blocking API call - runs in background while game is playable
        try {
            console.log("ClassifyScene: Attempting to fetch enhanced words from Claude API (non-blocking)");
            
            // Add 5-second timeout instead of 15-second
            const timeoutDuration = 5000;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
                console.log("ClassifyScene: Claude API timeout after 5 seconds, continuing with fallback words");
                controller.abort();
            }, timeoutDuration);
            
            const claudeWords = await Promise.race([
                this.claudeService.generateClassificationWords(WORDS_TO_CLASSIFY, CATEGORY_1, CATEGORY_2),
                new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('API timeout')), timeoutDuration);
                })
            ]);
            
            clearTimeout(timeoutId);
            
            if (claudeWords && claudeWords.length > 0) {
                console.log("ClassifyScene: Successfully received enhanced words from Claude API");
                // Could optionally update words for next round or show notification
                // For now, just log success - current game continues with fallback words
            }
        } catch (error) {
            console.log("ClassifyScene: Claude API failed, continuing with fallback words:", error.message);
            // Game continues normally with fallback words already loaded
        }
    }

    createCategoryBoxes() {
        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const boxWidth = width * 0.35;
        const boxHeight = height * 0.4;
        const boxY = height * 0.70; // Position lower on the screen

        // Category 1 Box (Left)
        const box1X = width * 0.25;
        this.categoryBox1 = this.add.container(box1X, boxY);
        const bg1 = this.add.rectangle(0, 0, boxWidth, boxHeight, 0x113355, 0.7) // Dark Blueish
            .setStrokeStyle(2, MenuUIUtils.COLORS.secondary, 0.8);
        const label1 = this.createThemedText(0, -boxHeight / 2 - 20, CATEGORY_1, 'sizeLg', MenuUIUtils.COLORS.secondary);
        this.categoryBox1.add([bg1, label1]);
        this.categoryBox1.setSize(boxWidth, boxHeight); // Set container size
        this.uiElements.categoryBox1 = this.categoryBox1;

        // Drop Zone 1
        this.dropZone1 = this.add.zone(box1X, boxY, boxWidth, boxHeight).setRectangleDropZone(boxWidth, boxHeight);
        this.dropZone1.setData('category', CATEGORY_1);
        // Optional: Add graphics for drop zone visualization during debug
        // const graphics1 = this.add.graphics(); graphics1.lineStyle(2, 0x00ffff); graphics1.strokeRect(box1X - boxWidth/2, boxY - boxHeight/2, boxWidth, boxHeight);

        // Category 2 Box (Right)
        const box2X = width * 0.75;
        this.categoryBox2 = this.add.container(box2X, boxY);
        const bg2 = this.add.rectangle(0, 0, boxWidth, boxHeight, 0x115533, 0.7) // Dark Greenish
            .setStrokeStyle(2, MenuUIUtils.COLORS.primary, 0.8);
        const label2 = this.createThemedText(0, -boxHeight / 2 - 20, CATEGORY_2, 'sizeLg', MenuUIUtils.COLORS.primary);
        this.categoryBox2.add([bg2, label2]);
        this.categoryBox2.setSize(boxWidth, boxHeight); // Set container size
        this.uiElements.categoryBox2 = this.categoryBox2;

        // Drop Zone 2
        this.dropZone2 = this.add.zone(box2X, boxY, boxWidth, boxHeight).setRectangleDropZone(boxWidth, boxHeight);
        this.dropZone2.setData('category', CATEGORY_2);
        // const graphics2 = this.add.graphics(); graphics2.lineStyle(2, 0x00ff00); graphics2.strokeRect(box2X - boxWidth/2, boxY - boxHeight/2, boxWidth, boxHeight);
    }

    // src/scenes/ClassifyScene.js

    displayNextWord() {
        // --- ADD SAFETY CHECK AT THE TOP ---
        if (!this || !this.scene || !this.cameras || !this.cameras.main || !this.scene.systems || !this.scene.systems.scenePlugin || !this.scene.systems.scenePlugin.isActive(this.scene.key)) {
            console.warn(`[${this.scene.key}] displayNextWord called on inactive or invalid scene. Aborting word display.`);
            return; // Exit if scene is not active or camera is missing
        }
        // --- END SAFETY CHECK ---

        if (this.currentWordIndex >= this.words.length) {
            this.gameOver();
            return;
        }

        const wordData = this.words[this.currentWordIndex];
        // --- Now it's safe to access camera properties ---
        const centerX = this.cameras.main.width / 2;
        const startY = this.cameras.main.height * 0.30;
        // --- End safety check context ---

        if (this.currentWordObject) {
             // Add safety check before destroying
             if (this.currentWordObject.scene) {
                try { this.currentWordObject.destroy(); } catch(e) { console.warn("Error destroying previous word object:", e); }
             }
            this.currentWordObject = null;
        }

        // --- ADD SAFETY CHECK before creating text ---
        if (!this.add) {
             console.error(`[${this.scene.key}] Scene 'add' factory missing in displayNextWord. Cannot create text.`);
             return;
        }
        // --- END SAFETY CHECK ---

        this.currentWordObject = this.add.text(centerX, startY, wordData.word, {
            fontFamily: 'monospace',
            fontSize: '32px', // Make word prominent
            color: '#FFFFFF',
            backgroundColor: '#222222',
            padding: { x: 15, y: 10 },
            stroke: MenuUIUtils.COLORS.primary_dark,
            strokeThickness: 1
        }).setOrigin(0.5).setInteractive({ useHandCursor: true });

        this.currentWordObject.setData('category', wordData.category);
        this.input.setDraggable(this.currentWordObject);

        // Animation for new word appearing
        this.currentWordObject.setScale(0.8).setAlpha(0);

        // --- ADD SAFETY CHECK before tweening ---
        if (this.tweens) {
            this.tweens.add({
                targets: this.currentWordObject,
                scale: 1,
                alpha: 1,
                duration: 300,
                ease: 'Back.easeOut'
            });
        } else {
             console.warn(`[${this.scene.key}] Tweens manager missing, setting word visible directly.`);
             if(this.currentWordObject) {
                this.currentWordObject.setScale(1).setAlpha(1);
             }
        }
        // --- END SAFETY CHECK ---

        console.log(`Displaying word ${this.currentWordIndex + 1}: "${wordData.word}" (Category: ${wordData.category})`);
    }

    handleWordDrop(isCorrect, wordObject) {
        if (!wordObject || !wordObject.active) return;

        wordObject.input.enabled = false; // Disable interaction

        if (isCorrect) {
            this.score++;
            if(this.uiElements.scoreText) this.uiElements.scoreText.setText(`Score: ${this.score}`);
            this.showFeedback("Correct!", MenuUIUtils.COLORS.success);
            // Correct animation: Quick shrink/fade
            this.tweens.add({
                targets: wordObject,
                scale: 0.1,
                alpha: 0,
                duration: 250,
                ease: 'Power1',
                onComplete: () => {
                    wordObject.destroy();
                    this.currentWordObject = null;
                    this.currentWordIndex++;
                    this.displayNextWord();
                }
            });
        } else {
            this.showFeedback("Wrong!", MenuUIUtils.COLORS.danger);
            // Incorrect animation: Shake and snap back
            this.tweens.add({
                targets: wordObject,
                x: wordObject.getData('startX'), // Use stored start position
                y: wordObject.getData('startY'),
                angle: { from: -5, to: 5, duration: 50, yoyo: true, repeat: 3 },
                duration: 300,
                ease: 'Quad.easeOut',
                onComplete: () => {
                    wordObject.input.enabled = true; // Re-enable after snap back
                    wordObject.setAngle(0); // Reset angle
                }
            });
        }
    }

    showFeedback(message, color) {
        if (this.feedbackText && this.feedbackText.active) {
            this.feedbackText.destroy(); // Remove previous feedback
        }
        const centerX = this.cameras.main.width / 2;
        const feedbackY = this.cameras.main.height * 0.45; // Position between word and boxes

        this.feedbackText = this.add.text(centerX, feedbackY, message, {
            fontFamily: 'monospace',
            fontSize: '28px',
            color: Phaser.Display.Color.ValueToColor(color).rgba,
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5).setAlpha(0).setScale(1.2);

        // Fade in, hold, fade out
        this.tweens.add({
            targets: this.feedbackText,
            alpha: 1,
            scale: 1,
            duration: 200,
            ease: 'Power1',
            onComplete: () => {
                this.time.delayedCall(600, () => {
                     if (this.feedbackText && this.feedbackText.active) {
                         this.tweens.add({
                             targets: this.feedbackText,
                             alpha: 0,
                             duration: 200,
                             onComplete: () => {
                                 if (this.feedbackText && this.feedbackText.active) this.feedbackText.destroy();
                                 this.feedbackText = null;
                             }
                         });
                     }
                });
            }
        });
    }

// --- MODIFIED gameOver Method ---
 // --- MODIFIED gameOver Method ---
 async gameOver() { // Ensure async keyword is present
    const sceneKey = this.scene.key;
    console.log(`[${sceneKey}] Game Over! Final Score: ${this.score}`);

    // Clean up the current word object
    if (this.currentWordObject && this.currentWordObject.active) {
        try { this.currentWordObject.destroy(); } catch(e){}
        this.currentWordObject = null;
    }

    // --- Determine Player Identity for Saving (Keep this logic) ---
    const user = auth.currentUser;
    const specialAccess = hasSpecialAccess();
    let finalUserId = null;
    let finalPlayerName = 'Guest';
    let isGuestScore = true; // Assume guest unless proven otherwise

    if (user && user.emailVerified) {
         isGuestScore = false;
         finalUserId = user.uid;
         try {
             const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
             finalPlayerName = (profileDoc.exists && profileDoc.data().displayName)
                 ? profileDoc.data().displayName : (user.displayName || user.email || "Player");
         } catch (e) {
             console.warn(`[${sceneKey}] Error fetching profile for score save:`, e);
             finalPlayerName = user.displayName || user.email || "Player";
         }
    } else if (specialAccess) {
         isGuestScore = true;
         finalUserId = getSpecialAccessUserId();
         finalPlayerName = getSpecialAccessDisplayName();
    } else {
         isGuestScore = true;
         finalPlayerName = localStorage.getItem("tempSimpsonsName") || `Guest-${Math.floor(Math.random() * 10000)}`;
         finalUserId = `guest_${finalPlayerName}`; // Assign temporary guest ID
         if (!localStorage.getItem("tempSimpsonsName")) {
             localStorage.setItem("tempSimpsonsName", finalPlayerName);
         }
    }
    console.log(`[${sceneKey}] Determined score save identity - ID: ${finalUserId}, Name: ${finalPlayerName}, IsGuest: ${isGuestScore}`);
    // --- End Player Identity ---

    // --- Prepare Score Data ---
    const scoreData = {
        score: this.score,
        game: "ClassifyTech", // Ensure correct game type
        player: finalPlayerName,
        userUID: finalUserId,
    };
    // --- End Prepare Score Data ---

    // --- !!! CHANGE: Await the score save !!! ---
    let saveSuccess = false;
    try {
        console.log(`[${sceneKey}] Attempting to save score (awaiting)... Data:`, scoreData);
        // Use await here and store the result
        saveSuccess = await saveScoreToFirestore(scoreData, true); // true = skip overlay message
        if (saveSuccess) {
            console.log(`[${sceneKey}] Score save operation completed successfully.`);
            
            // NEW: Record ranking snapshot for Hall of Fame trends
            if (window.HallOfFameRankingSystem && !isGuestScore && finalUserId) {
              try {
                await window.HallOfFameRankingSystem.recordPlayerRankingSnapshot(
                  finalUserId, 
                  'ClassifyTech', 
                  this.score
                );
                console.log(`📈 Ranking snapshot recorded for trends: ${finalUserId}, ClassifyTech, ${this.score}`);
              } catch (rankingError) {
                console.warn('📈 Ranking snapshot failed (non-critical):', rankingError);
              }
            }
        } else {
            console.warn(`[${sceneKey}] Score save operation returned false (failed or skipped).`);
        }
    } catch (err) {
        // Catch errors during the save itself
        console.error(`[${sceneKey}] Error DURING score save:`, err);
        // Optionally show a user message here if saving is critical before proceeding
        // For now, we'll still transition, but the score might not be on the leaderboard
    }
    // --- !!! END CHANGE !!! ---

    // --- Transition AFTER awaiting the save attempt ---
    console.log(`[${sceneKey}] Transitioning to ClassifyTechGameOverScene`);
    sceneManager.switchToScene('ClassifyTechGameOverScene', {
        score: this.score,
        playerDisplayName: finalPlayerName // Pass name for immediate display
    });
}
// --- End MODIFIED gameOver Method ---

     returnToPreviousMenu() {
         const user = auth.currentUser;
         const specialAccess = hasSpecialAccess();
         const isEffectivelyLoggedIn = (user && user.emailVerified) || specialAccess;
         
         // CRITICAL FIX: Use feature flag routing instead of hard-coded scene names
         const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
         const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
         const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
         
         let targetScene;
         if (useUniversalLanding) {
             targetScene = 'MainMenuSceneUniversal';
         } else if (isEffectivelyLoggedIn) {
             targetScene = useDOMAuthMenu ? "AuthenticatedMenuSceneDOM" : "AuthenticatedMenuScene";
         } else {
             // CRITICAL FIX: Always use DOM or Universal scenes
             const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
             targetScene = useUniversalLanding ? "MainMenuSceneUniversal" : "GuestMenuSceneDOM";
         }
         
         console.log(`ClassifyScene: Returning to menu - ${targetScene}`);
         this.startScene(targetScene);
     }

    handleResize(gameSize) {
        super.handleResize(gameSize); // Handles background
        if (!this.cameras?.main) return;

        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const centerX = width / 2;

        // Reposition elements
        if (this.uiElements.title) this.uiElements.title.setPosition(centerX, 50);
        if (this.uiElements.scoreText) this.uiElements.scoreText.setPosition(width - this.padding, this.padding);
        if (this.uiElements.loadingText) this.uiElements.loadingText.setPosition(centerX, height / 2);
        if (this.uiElements.backButton) this.uiElements.backButton.setPosition(this.padding + 60, this.padding + 20);

        const boxWidth = width * 0.35;
        const boxHeight = height * 0.4;
        const boxY = height * 0.70;
        const box1X = width * 0.25;
        const box2X = width * 0.75;

        if (this.categoryBox1) {
            this.categoryBox1.setPosition(box1X, boxY);
            // Note: Container children positions are relative, but zone needs absolute
            if (this.dropZone1) {
                this.dropZone1.x = box1X;
                this.dropZone1.y = boxY;
                // Resize zone if needed (setSize not available, use setRectangleDropZone)
                 this.dropZone1.input.hitArea.width = boxWidth;
                 this.dropZone1.input.hitArea.height = boxHeight;
            }
        }
        if (this.categoryBox2) {
            this.categoryBox2.setPosition(box2X, boxY);
            if (this.dropZone2) {
                this.dropZone2.x = box2X;
                this.dropZone2.y = boxY;
                 this.dropZone2.input.hitArea.width = boxWidth;
                 this.dropZone2.input.hitArea.height = boxHeight;
            }
        }

        // Reposition current word if it exists and isn't being dragged
        if (this.currentWordObject && !this.currentWordObject.getData('isDragging')) {
            const startY = height * 0.30;
            this.currentWordObject.setPosition(centerX, startY);
             // Update snap-back position
             this.currentWordObject.setData('startX', centerX);
             this.currentWordObject.setData('startY', startY);
        }
    }

    shutdown() {
        console.log(`[${this.scene.key}] Shutting down...`);
        // Clear timers, tweens, listeners
        if (this.feedbackText && this.feedbackText.active) { this.feedbackText.destroy(); }
        if (this.currentWordObject && this.currentWordObject.active) { this.currentWordObject.destroy(); }
        // Destroy zones and containers explicitly if needed, though super.shutdown should help
        if(this.dropZone1) this.dropZone1.destroy();
        if(this.dropZone2) this.dropZone2.destroy();
        if(this.categoryBox1) this.categoryBox1.destroy(true); // Destroy container and children
        if(this.categoryBox2) this.categoryBox2.destroy(true);

        super.shutdown(); // Call parent shutdown LAST
        console.log(`[${this.scene.key}] Shutdown complete.`);
    }
}

export default ClassifyScene;